import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Languages as LanguagesIcon, Globe2, TrendingUp, Search,
  StickyNote, AlertTriangle, MessageSquare, Send, Filter,
  Bookmark, Flag, BookText, PackageCheck, X, Plus, Link as LinkIcon,
  Columns as ColumnsIcon, Check,
} from "lucide-react";

const STATUS_COLORS = {
  Active: "var(--on-track)",
  "In Progress": "var(--on-track)",
  Development: "var(--amber)",
  Relationship: "var(--amber)",
  Investigation: "#6a5b8f",
  Ended: "var(--behind)",
  Paused: "var(--behind)",
};

const SCOPE_PRODUCTS = ["New Testament", "Old Testament", "Bible Stories"];

const ALL_COLUMNS = [
  { key: "language",       label: "Language",           always: true },
  { key: "iso",            label: "ISO · RoLV" },
  { key: "country",        label: "Country" },
  { key: "cluster",        label: "Cluster" },
  { key: "hub",            label: "Hub" },
  { key: "internal_project", label: "Internal Project" },
  { key: "flag",           label: "Flag" },
  { key: "organizations",  label: "Organizations" },
  { key: "involvement",    label: "Involvement" },
  { key: "type",           label: "Type / Scope" },
  { key: "current_scope",  label: "Current Scope" },
  { key: "completed",      label: "Completed Products" },
  { key: "status",         label: "Status" },
  { key: "progress",       label: "Progress" },
  { key: "dates",          label: "Dates" },
  { key: "notes",          label: "Notes" },
  { key: "issues",         label: "Issues" },
];

const COLUMNS_STORAGE_KEY = "bt.languages.columns";

function loadVisibility() {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, parsed[c.key] ?? true]));
  } catch { return null; }
}
function saveVisibility(v) {
  try { localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

export default function LanguagesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [engagements, setEngagements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [colFilters, setColFilters] = useState({}); // { columnKey: [values] }
  const [visible, setVisible] = useState(() =>
    loadVisibility() || Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, true]))
  );
  const [adminClusters, setAdminClusters] = useState([]);
  const [hubOptions, setHubOptions] = useState([]);
  const [internalProjects, setInternalProjects] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [scopeEditor, setScopeEditor] = useState(null);

  const load = async () => {
    setLoading(true);
    const [e, s] = await Promise.all([
      api.get("/languages/engagements", { params: { search } }),
      api.get("/languages/engagement-stats"),
    ]);
    setEngagements(e.data);
    setStats(s.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => {
    api.get("/languages/cluster-options").then((r) => setAdminClusters(r.data || []));
    api.get("/languages/hub-options").then((r) => setHubOptions(r.data || []));
    api.get("/languages/internal-project-options").then((r) => setInternalProjects(r.data || []));
  }, []);
  useEffect(() => { saveVisibility(visible); }, [visible]);

  const openDrawer = (engagement, kind) => setDrawer({ engagement, kind });

  const setEngagementCluster = async (key, newCluster) => {
    try {
      await api.patch(`/languages/engagements/${encodeURIComponent(key)}/cluster`, { cluster: newCluster || null });
      setEngagements((rows) => rows.map((r) => (r.key === key ? { ...r, cluster: newCluster || null } : r)));
      toast.success(newCluster ? `Cluster set to ${newCluster}` : "Cluster cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  const setEngagementHub = async (key, newHub) => {
    try {
      await api.patch(`/languages/engagements/${encodeURIComponent(key)}/hub`, { hub: newHub || null });
      setEngagements((rows) => rows.map((r) => (r.key === key ? { ...r, hub: newHub || null } : r)));
      toast.success(newHub ? `Hub set to ${newHub}` : "Hub cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  const setEngagementInternalProject = async (key, newVal) => {
    try {
      await api.patch(`/languages/engagements/${encodeURIComponent(key)}/internal-project`, { internal_project: newVal || null });
      setEngagements((rows) => rows.map((r) => (r.key === key ? { ...r, internal_project: newVal || null } : r)));
      toast.success(newVal ? `Internal project set to ${newVal}` : "Internal project cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  const setEngagementFlag = async (key, newVal) => {
    try {
      await api.patch(`/languages/engagements/${encodeURIComponent(key)}/flag`, { flag: newVal || null });
      setEngagements((rows) => rows.map((r) => (r.key === key ? { ...r, flag: newVal || null } : r)));
      toast.success(newVal ? `Flagged as ${newVal.replace(/_/g, ' ')}` : "Flag cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  // Compute unique values for each filterable column
  const uniques = useMemo(() => {
    const u = {};
    for (const c of ALL_COLUMNS) u[c.key] = new Set();
    for (const e of engagements) {
      u.language.add(e.language_name || "");
      u.iso.add(e.iso || "");
      u.country.add(e.country || "");
      u.cluster.add(e.cluster || "");
      u.hub.add(e.hub || "");
      u.internal_project.add(e.internal_project || "");
      u.flag.add(e.flag || "");
      (e.organizations || []).forEach((o) => u.organizations.add(o));
      (e.engagement_functions || []).forEach((o) => u.involvement.add(o));
      (e.activities || []).forEach((o) => u.type.add(o));
      (e.current_scope || []).forEach((o) => u.current_scope.add(o));
      (e.completed_products || []).forEach((o) => u.completed.add(o.product));
      (e.statuses || []).forEach((s) => u.status.add(s));
    }
    return Object.fromEntries(Object.entries(u).map(([k, v]) => [k, [...v].filter(Boolean).sort()]));
  }, [engagements]);

  const rows = useMemo(() => engagements.filter((e) => {
    for (const [k, vals] of Object.entries(colFilters)) {
      if (!vals?.length) continue;
      switch (k) {
        case "language":       if (!vals.includes(e.language_name || "")) return false; break;
        case "iso":            if (!vals.includes(e.iso || "")) return false; break;
        case "country":        if (!vals.includes(e.country || "")) return false; break;
        case "cluster":        if (!vals.includes(e.cluster || "")) return false; break;
        case "hub":            if (!vals.includes(e.hub || "")) return false; break;
        case "organizations":  if (!(e.organizations || []).some((o) => vals.includes(o))) return false; break;
        case "involvement":    if (!(e.engagement_functions || []).some((o) => vals.includes(o))) return false; break;
        case "type":           if (!(e.activities || []).some((o) => vals.includes(o))) return false; break;
        case "current_scope":  if (!(e.current_scope || []).some((o) => vals.includes(o))) return false; break;
        case "completed":      if (!(e.completed_products || []).some((p) => vals.includes(p.product))) return false; break;
        case "status":         if (!(e.statuses || []).some((s) => vals.includes(s))) return false; break;
        default: break;
      }
    }
    return true;
  }), [engagements, colFilters]);

  const show = (k) => visible[k];
  const activeFilterCols = Object.entries(colFilters).filter(([, v]) => v?.length).map(([k]) => k);

  return (
    <div className="max-w-[1900px] mx-auto px-10 py-10 fade-in-up" data-testid="languages-page">
      <div className="mb-6">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Language Engagements · ISO × RoLV × Country
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Languages</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-3xl">
          Every unique language engagement, aggregated by ISO code + dialect (RoLV) + country. Use the
          funnel on any header for Excel-style filtering, and <b>Columns</b> to hide / show fields.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[color:var(--border)] border border-[color:var(--border)] mb-6">
          <MiniKpi icon={LanguagesIcon} label="Language Engagements" value={stats.total_engagements} testid="kpi-total" />
          <MiniKpi icon={TrendingUp} label="Active" value={stats.in_progress} tone="ontrack" testid="kpi-in-progress" />
          <MiniKpi icon={Globe2} label="Countries" value={stats.total_countries} testid="kpi-countries" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex-1 min-w-64 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <input
            className="input-flat pl-9"
            placeholder="Search language, ISO, RoLV, country…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="lang-search"
          />
        </div>
        <button
          className="btn-ghost text-xs flex items-center gap-1"
          onClick={() => { setColFilters({}); setSearch(""); }}
          data-testid="filter-clear"
        >
          <Filter size={12} /> Clear filters
        </button>
        <ColumnsToggle visible={visible} onChange={setVisible} />
      </div>

      <div className="mono text-[11px] text-[color:var(--ink-2)] mb-3 uppercase tracking-widest flex flex-wrap items-center gap-3">
        <span>{loading ? "Loading…" : `${rows.length} of ${engagements.length} engagements`}</span>
        {activeFilterCols.length > 0 && (
          <span className="text-[color:var(--accent)]">
            · active: {activeFilterCols.map((k) => ALL_COLUMNS.find((c) => c.key === k)?.label).join(", ")}
          </span>
        )}
      </div>

      <div className="hairline bg-white overflow-x-auto">
        <table className="w-full data-table min-w-[1700px]" data-testid="engagements-table">
          <thead>
            <tr>
              {ALL_COLUMNS.filter((c) => show(c.key)).map((c) => (
                <HeaderCell
                  key={c.key}
                  column={c}
                  values={uniques[c.key] || []}
                  filters={colFilters}
                  setFilters={setColFilters}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.key} data-testid={`row-${e.iso}-${e.rolv || 'na'}`}>
                {show("language") && (
                  <td className="min-w-40 font-medium">{e.language_name || <span className="text-[color:var(--ink-3)] italic">Unknown</span>}</td>
                )}
                {show("iso") && (
                  <td className="mono text-xs whitespace-nowrap">
                    <span>{e.iso}</span>
                    {e.rolv && <span className="text-[color:var(--ink-3)]"> · {e.rolv}</span>}
                  </td>
                )}
                {show("country") && <td className="text-sm whitespace-nowrap">{e.country || "—"}</td>}
                {show("cluster") && (
                  <td>
                    {isAdmin ? (
                      <select
                        className="input-flat text-xs py-1 min-w-[8rem]"
                        value={e.cluster || ""}
                        onChange={(ev) => setEngagementCluster(e.key, ev.target.value)}
                        data-testid={`cluster-select-${e.iso}-${e.rolv || 'na'}`}
                      >
                        <option value="">—</option>
                        {adminClusters.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <span className="text-xs">{e.cluster || <span className="text-[color:var(--ink-3)]">—</span>}</span>
                    )}
                  </td>
                )}
                {show("hub") && (
                  <td>
                    {isAdmin ? (
                      <select
                        className="input-flat text-xs py-1 min-w-[6rem]"
                        value={e.hub || ""}
                        onChange={(ev) => setEngagementHub(e.key, ev.target.value)}
                        data-testid={`hub-select-${e.iso}-${e.rolv || 'na'}`}
                      >
                        <option value="">—</option>
                        {hubOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    ) : (
                      <span className="mono text-xs">{e.hub || <span className="text-[color:var(--ink-3)]">—</span>}</span>
                    )}
                  </td>
                )}
                {show("internal_project") && (
                  <td>
                    {isAdmin ? (
                      <select
                        className="input-flat text-xs py-1 min-w-[8rem]"
                        value={e.internal_project || ""}
                        onChange={(ev) => setEngagementInternalProject(e.key, ev.target.value)}
                        data-testid={`internal-project-select-${e.iso}-${e.rolv || 'na'}`}
                      >
                        <option value="">—</option>
                        {internalProjects.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    ) : (
                      <span className="text-xs">{e.internal_project || <span className="text-[color:var(--ink-3)]">—</span>}</span>
                    )}
                  </td>
                )}
                {show("flag") && (
                  <td>
                    {isAdmin ? (
                      <select
                        className="input-flat text-xs py-1 min-w-[9rem]"
                        value={e.flag || ""}
                        onChange={(ev) => setEngagementFlag(e.key, ev.target.value)}
                        data-testid={`flag-select-${e.iso}-${e.rolv || 'na'}`}
                        style={e.flag ? { borderColor: "var(--amber)", color: "var(--amber)" } : undefined}
                      >
                        <option value="">— none —</option>
                        <option value="duplicate">Duplicate</option>
                        <option value="marked_for_deletion">Marked for deletion</option>
                        <option value="invalid">Invalid</option>
                      </select>
                    ) : (
                      <FlagPill flag={e.flag} />
                    )}
                  </td>
                )}
                {show("organizations") && (
                  <td className="text-xs">
                    {(e.organizations || []).slice(0, 2).map((o, i) => <div key={i}>{o}</div>)}
                    {(e.organizations || []).length > 2 && (
                      <div className="text-[color:var(--ink-3)]">+{e.organizations.length - 2} more</div>
                    )}
                  </td>
                )}
                {show("involvement") && <td className="text-xs">{(e.engagement_functions || []).join(" · ") || "—"}</td>}
                {show("type") && (
                  <td className="text-xs">
                    <div>{(e.activities || []).join(", ") || "—"}</div>
                    <div className="text-[color:var(--ink-3)] mt-0.5">{(e.translation_goals || []).join(", ")}</div>
                  </td>
                )}
                {show("current_scope") && (
                  <td>
                    <CurrentScopeCell
                      values={e.current_scope || []}
                      isAdmin={isAdmin}
                      onEdit={() => setScopeEditor(e)}
                      testId={`current-scope-${e.iso}-${e.rolv || 'na'}`}
                    />
                  </td>
                )}
                {show("completed") && (
                  <td>
                    <CompletedProductsCell
                      products={e.completed_products || []}
                      isAdmin={isAdmin}
                      onEdit={() => setScopeEditor(e)}
                      testId={`completed-${e.iso}-${e.rolv || 'na'}`}
                    />
                  </td>
                )}
                {show("status") && (
                  <td>
                    {(e.statuses || []).map((s) => (
                      <span key={s} className="chip" style={{ borderColor: STATUS_COLORS[s] || "var(--border)", color: STATUS_COLORS[s] || "var(--ink)" }}>
                        {s}
                      </span>
                    ))}
                  </td>
                )}
                {show("progress") && (
                  <td className="text-right mono text-sm">
                    {e.progress != null ? `${Math.round(e.progress * 100)}%` : <span className="text-[color:var(--ink-3)]">—</span>}
                  </td>
                )}
                {show("dates") && (
                  <td className="mono text-[10px] whitespace-nowrap">
                    <div><span className="text-[color:var(--ink-3)]">Start</span> {e.actual_begin || e.planned_begin || "—"}</div>
                    <div><span className="text-[color:var(--ink-3)]">End</span> {e.actual_end || e.planned_end || "—"}</div>
                  </td>
                )}
                {show("notes") && (
                  <td className="text-center">
                    <FlagMarker
                      active={!!e.has_notes}
                      count={e.notes_count || 0}
                      icon={Bookmark}
                      activeColor="var(--on-track)"
                      onClick={() => openDrawer(e, "notes")}
                      testId={`notes-btn-${e.iso}-${e.rolv || 'na'}`}
                      labelSingular="note"
                    />
                  </td>
                )}
                {show("issues") && (
                  <td className="text-center">
                    <FlagMarker
                      active={!!e.has_issues}
                      count={e.issues_count || 0}
                      icon={Flag}
                      activeColor="var(--amber)"
                      onClick={() => openDrawer(e, "issues")}
                      testId={`issues-btn-${e.iso}-${e.rolv || 'na'}`}
                      labelSingular="issue"
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NotesDrawer drawer={drawer} onClose={() => setDrawer(null)} onSaved={load} />
      <ScopeEditorDialog
        key={scopeEditor?.key || "closed"}
        engagement={scopeEditor}
        onClose={() => setScopeEditor(null)}
        onLocalUpdate={(k, patch) => setEngagements((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)))}
        onSaved={load}
      />
    </div>
  );
}

/* ─────────────────  Reusable pieces  ───────────────── */

function ColumnsToggle({ visible, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="btn-ghost text-xs flex items-center gap-1.5" data-testid="columns-toggle">
          <ColumnsIcon size={13} /> Columns
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] px-1 pb-2">
          Show Columns
        </div>
        <div className="max-h-72 overflow-y-auto">
          {ALL_COLUMNS.map((c) => (
            <label
              key={c.key}
              className={`flex items-center gap-2 px-1.5 py-1 text-xs cursor-pointer hover:bg-[color:var(--surface-2)] ${c.always ? "opacity-60" : ""}`}
            >
              <input
                type="checkbox"
                className="accent-[color:var(--ink)]"
                checked={visible[c.key]}
                disabled={c.always}
                onChange={() => onChange({ ...visible, [c.key]: !visible[c.key] })}
                data-testid={`col-toggle-${c.key}`}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
        <div className="pt-2 border-t border-[color:var(--border)] mt-2 flex justify-between gap-2">
          <button
            className="btn-ghost text-xs"
            onClick={() => onChange(Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, true])))}
          >
            Show all
          </button>
          <button
            className="btn-ghost text-xs"
            onClick={() => onChange(Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, !!c.always])))}
          >
            Minimal
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function HeaderCell({ column, values, filters, setFilters }) {
  const active = filters[column.key] || [];
  const [q, setQ] = useState("");
  const [checked, setChecked] = useState(new Set(active));
  const filtered = values.filter((v) => (v || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => setChecked((s) => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });
  const align = (column.key === "progress") ? "text-right" : "";

  return (
    <th className={`!py-2 ${align}`}>
      <div className="inline-flex items-center gap-1">
        <span>{column.label}</span>
        <Popover onOpenChange={(o) => { if (o) setChecked(new Set(active)); setQ(""); }}>
          <PopoverTrigger asChild>
            <button
              className={`p-0.5 border transition ${active.length ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-transparent text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"}`}
              title="Filter"
              data-testid={`hdr-filter-${column.key}`}
            >
              <Filter size={10} strokeWidth={active.length ? 2.5 : 1.5} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] px-1 pb-2">
              Filter · {column.label}
            </div>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
              <input
                className="input-flat text-xs py-1 pl-7 w-full"
                placeholder="Search values…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="max-h-52 overflow-y-auto mt-2">
              {filtered.length === 0 && <div className="text-xs text-[color:var(--ink-3)] italic px-2 py-3">No matches</div>}
              {filtered.map((v) => (
                <label key={v} className="flex items-center gap-2 px-1.5 py-1 text-xs cursor-pointer hover:bg-[color:var(--surface-2)]">
                  <input type="checkbox" className="accent-[color:var(--ink)]" checked={checked.has(v)} onChange={() => toggle(v)} />
                  <span className="truncate flex-1" title={v}>{v}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between gap-2 pt-2 border-t border-[color:var(--border)] mt-2">
              <button
                className="btn-ghost text-xs"
                onClick={() => { setChecked(new Set()); setFilters({ ...filters, [column.key]: [] }); }}
                data-testid={`hdr-filter-clear-${column.key}`}
              >
                Clear
              </button>
              <button
                className="btn-primary text-xs"
                onClick={() => setFilters({ ...filters, [column.key]: [...checked] })}
                data-testid={`hdr-filter-apply-${column.key}`}
              >
                <Check size={11} className="inline mr-1" />Apply
              </button>
            </div>
          </PopoverContent>
        </Popover>
        {active.length > 0 && (
          <span
            className="mono text-[9px] px-1.5 py-0.5 rounded-sm cursor-pointer"
            style={{ background: "rgba(107,111,150,0.12)", color: "var(--accent)" }}
            title="Clear filter"
            onClick={() => setFilters({ ...filters, [column.key]: [] })}
          >
            {active.length}<X size={9} className="inline ml-0.5" />
          </span>
        )}
      </div>
    </th>
  );
}

function CurrentScopeCell({ values, isAdmin, onEdit, testId }) {
  if (!values.length) {
    return isAdmin ? (
      <button className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--ink)] underline underline-offset-4"
        onClick={onEdit} data-testid={testId}>+ set</button>
    ) : <span className="text-[color:var(--ink-3)] text-xs">—</span>;
  }
  const chips = (
    <div className="flex flex-wrap gap-0.5" data-testid={testId}>
      {values.map((v) => (
        <span key={v}
          className="mono px-1 py-0.5 border rounded-sm"
          style={{ fontSize: "9px", lineHeight: 1.2, borderColor: "var(--accent)", color: "var(--accent)" }}
        >
          {v}
        </span>
      ))}
    </div>
  );
  return isAdmin ? (
    <button onClick={onEdit} className="text-left hover:opacity-80">{chips}</button>
  ) : chips;
}

function CompletedProductsCell({ products, isAdmin, onEdit, testId }) {
  if (!products.length) {
    return isAdmin ? (
      <button className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--ink)] underline underline-offset-4"
        onClick={onEdit} data-testid={testId}>+ add</button>
    ) : <span className="text-[color:var(--ink-3)] text-xs">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {products.map((p, i) => (
        <span key={i} className="chip inline-flex items-center gap-1"
          style={{ borderColor: "var(--on-track)", color: "var(--on-track)" }}>
          <PackageCheck size={10} />
          {p.url ? (
            <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline" onClick={(e) => e.stopPropagation()}>{p.product}</a>
          ) : p.product}
        </span>
      ))}
      {isAdmin && (
        <button onClick={onEdit}
          className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] mono text-[10px] ml-1"
          data-testid={`${testId}-edit`}>edit</button>
      )}
    </div>
  );
}

function FlagMarker({ active, count, icon: Icon, activeColor, onClick, testId, labelSingular }) {
  const title = count > 0 ? `${count} ${labelSingular}${count === 1 ? "" : "s"}` : `Add a ${labelSingular}`;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      title={title}
      className="inline-flex items-center justify-center gap-1.5 w-16 h-8 mx-auto transition hover:opacity-70"
    >
      <Icon size={22} strokeWidth={active ? 0 : 1.75} fill={active ? activeColor : "none"} color={active ? activeColor : "var(--ink-3)"} />
      <span className="mono text-xs font-semibold leading-none tabular-nums w-3 text-left" style={{ color: activeColor }}>
        {active && count > 0 ? count : ""}
      </span>
    </button>
  );
}

function MiniKpi({ icon: Icon, label, value, tone, testid }) {
  const color = tone === "ontrack" ? "text-[color:var(--on-track)]" : "";
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">{label}</div>
        <Icon size={16} className="text-[color:var(--ink-3)]" strokeWidth={1.5} />
      </div>
      <div className={`serif text-4xl tracking-tight ${color}`}>{value.toLocaleString()}</div>
    </div>
  );
}

function NotesDrawer({ drawer, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const { user } = useAuth();
  const kind = drawer?.kind;
  const isNotes = kind === "notes";

  useEffect(() => {
    if (!drawer) return;
    setText("");
    api.get(`/languages/engagements/${encodeURIComponent(drawer.engagement.key)}`).then((r) => setEngagement(r.data));
  }, [drawer]);

  if (!drawer) return null;
  const items = (engagement?.[isNotes ? "notes" : "issues"] || []).slice().reverse();

  const save = async () => {
    if (!text.trim()) return toast.error(`Enter a ${isNotes ? "note" : "concern"} first`);
    setSaving(true);
    try {
      const url = `/languages/engagements/${encodeURIComponent(drawer.engagement.key)}/${isNotes ? "notes" : "issues"}`;
      await api.post(url, { text });
      setText("");
      const fresh = await api.get(`/languages/engagements/${encodeURIComponent(drawer.engagement.key)}`);
      setEngagement(fresh.data);
      onSaved();
      toast.success(isNotes ? "Note added" : "Concern added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!drawer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="notes-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-3xl flex items-center gap-2">
            {isNotes ? <StickyNote size={20} /> : <AlertTriangle size={20} className="text-[color:var(--amber)]" />}
            {isNotes ? "Language Notes" : "Concerns / Issues"}
          </DialogTitle>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mt-1">
            {drawer.engagement.language_name} · {drawer.engagement.iso}{drawer.engagement.rolv ? ` · ${drawer.engagement.rolv}` : ""} · {drawer.engagement.country}
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <textarea
            className="input-flat" rows={4}
            placeholder={isNotes ? "Add a language note (context, decisions, observations)…" : "Describe a concern or blocker…"}
            value={text} onChange={(e) => setText(e.target.value)}
            data-testid="note-input"
          />
          <div className="flex justify-between items-center">
            <div className="mono text-[10px] text-[color:var(--ink-3)]">
              Signed as <span className="text-[color:var(--ink-2)]">{user?.email}</span>
            </div>
            <button className="btn-primary text-sm flex items-center gap-2" onClick={save} disabled={saving} data-testid="note-save">
              <Send size={13} /> {saving ? "Saving…" : `Add ${isNotes ? "Note" : "Concern"}`}
            </button>
          </div>
        </div>

        <div className="border-t border-[color:var(--border)] pt-4 space-y-3">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">History ({items.length})</div>
          {items.length === 0 ? (
            <div className="text-sm text-[color:var(--ink-2)] italic">No entries yet.</div>
          ) : items.map((it, dispI) => {
            const originalIndex = items.length - 1 - dispI;
            const isDeleted = !!it.deleted_at;
            const canDelete = !isDeleted && (user?.role === "admin" || user?.role === "project_manager" || it.user === user?.email);
            const doDelete = async () => {
              if (!window.confirm(`Delete this ${isNotes ? "note" : "issue"}? It will remain visible with a strikethrough.`)) return;
              try {
                const url = `/languages/engagements/${encodeURIComponent(drawer.engagement.key)}/${isNotes ? "notes" : "issues"}/${originalIndex}`;
                await api.delete(url);
                const fresh = await api.get(`/languages/engagements/${encodeURIComponent(drawer.engagement.key)}`);
                setEngagement(fresh.data);
                onSaved();
                toast.success("Marked as deleted");
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Failed");
              }
            };
            return (
              <div key={dispI} className="border-l-2 pl-3 py-1" style={{ borderColor: isDeleted ? "var(--behind)" : (isNotes ? "var(--on-track)" : "var(--amber)") }} data-testid={`entry-${dispI}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className={`text-sm whitespace-pre-wrap flex-1 ${isDeleted ? "line-through" : ""}`}
                    style={isDeleted ? { color: "var(--behind)" } : undefined}>
                    {it.text}
                  </div>
                  {canDelete && (
                    <button
                      onClick={doDelete}
                      className="text-[color:var(--ink-3)] hover:text-[color:var(--behind)] shrink-0"
                      title={`Delete ${isNotes ? "note" : "issue"}`}
                      data-testid={`delete-${isNotes ? "note" : "issue"}-${dispI}`}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="mono text-[10px] text-[color:var(--ink-3)] mt-1">
                  <MessageSquare size={9} className="inline mr-1" />
                  {it.user} · {new Date(it.created_at).toLocaleString()}
                </div>
                {isDeleted && (
                  <div className="mono text-[10px] mt-0.5" style={{ color: "var(--behind)" }} data-testid={`deleted-tag-${dispI}`}>
                    Deleted by {it.deleted_by} · {new Date(it.deleted_at).toLocaleString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScopeEditorDialog({ engagement, onClose, onSaved, onLocalUpdate }) {
  const [scope, setScope] = useState(() => engagement?.current_scope || []);
  const [products, setProducts] = useState(() => (engagement?.completed_products || []).map((p) => ({ ...p })));
  const [saving, setSaving] = useState(false);
  if (!engagement) return null;

  const toggleScope = (v) => setScope((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);
  const toggleProduct = (v) => setProducts((ps) => {
    const has = ps.find((p) => p.product === v);
    return has ? ps.filter((p) => p.product !== v) : [...ps, { product: v, url: "" }];
  });
  const setProductUrl = (v, url) => setProducts((ps) => ps.map((p) => p.product === v ? { ...p, url } : p));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = products.map((p) => ({ product: p.product, url: (p.url || "").trim() || null }));
      await api.patch(`/languages/engagements/${encodeURIComponent(engagement.key)}/scope`, {
        current_scope: scope, completed_products: cleaned,
      });
      onLocalUpdate?.(engagement.key, { current_scope: scope, completed_products: cleaned });
      toast.success("Scope updated");
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!engagement} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="scope-editor-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-3xl flex items-center gap-2">
            <BookText size={20} /> Project Scope
          </DialogTitle>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mt-1">
            {engagement.language_name} · {engagement.iso}{engagement.rolv ? ` · ${engagement.rolv}` : ""} · {engagement.country}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <section>
            <div className="mono text-[10px] uppercase tracking-widest mb-3">Current Scope</div>
            <div className="grid grid-cols-3 gap-2">
              {SCOPE_PRODUCTS.map((p) => {
                const on = scope.includes(p);
                return (
                  <label key={p}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm border ${on ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-[color:var(--border)] hover:border-[color:var(--border-strong)]"}`}
                    data-testid={`scope-toggle-${p.replace(/\s+/g, '-')}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleScope(p)} className="accent-[color:var(--ink)]" />
                    <span>{p}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="border-t border-[color:var(--border)] pt-6">
            <div className="mono text-[10px] uppercase tracking-widest mb-3 flex items-center gap-2">
              <PackageCheck size={12} /> Completed Products
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {SCOPE_PRODUCTS.map((p) => {
                const selected = !!products.find((x) => x.product === p);
                return (
                  <button
                    key={p} type="button" onClick={() => toggleProduct(p)}
                    className={`px-3 py-1.5 border rounded-full text-xs transition ${selected ? "bg-[color:var(--on-track)] text-white border-[color:var(--on-track)]" : "border-[color:var(--border)] text-[color:var(--ink-2)] hover:border-[color:var(--border-strong)]"}`}
                    data-testid={`completed-chip-${p.replace(/\s+/g, '-')}`}
                  >
                    {selected ? (<><PackageCheck size={11} className="inline mr-1" strokeWidth={2.5} />{p}</>) : (<><Plus size={11} className="inline mr-1" strokeWidth={2.5} />{p}</>)}
                  </button>
                );
              })}
            </div>
            {products.length === 0 ? (
              <div className="text-xs text-[color:var(--ink-3)] italic pl-1">Select a product above to include it. You can optionally attach a link.</div>
            ) : (
              <div className="space-y-1.5">
                {products.map((v) => (
                  <div key={v.product} className="flex items-center gap-2">
                    <span className="mono text-[10px] uppercase tracking-widest w-28 shrink-0 text-[color:var(--ink-2)]">{v.product}</span>
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <LinkIcon size={12} className="text-[color:var(--ink-3)] shrink-0" />
                      <input
                        className="input-flat text-xs py-1 flex-1 min-w-0"
                        placeholder="https://link-to-published-scripture (optional)"
                        value={v.url || ""} onChange={(e) => setProductUrl(v.product, e.target.value)}
                        data-testid={`completed-url-${v.product.replace(/\s+/g, '-')}`}
                      />
                    </div>
                    <button type="button" onClick={() => toggleProduct(v.product)}
                      className="text-[color:var(--ink-3)] hover:text-[color:var(--behind)]" title={`Remove ${v.product}`}
                      data-testid={`completed-remove-${v.product.replace(/\s+/g, '-')}`}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost text-sm" onClick={onClose} disabled={saving} data-testid="scope-cancel">Cancel</button>
            <button className="btn-primary text-sm" onClick={save} disabled={saving} data-testid="scope-save">
              {saving ? "Saving…" : "Save Scope"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function FlagPill({ flag }) {
  if (!flag) return <span className="text-[color:var(--ink-3)] mono text-[10px]">—</span>;
  const label = flag.replace(/_/g, " ");
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-block"
      style={{ borderColor: "var(--amber)", color: "var(--amber)" }}
    >
      {label}
    </span>
  );
}
